/**
 * AnalyticsAddTaskButton — Feb 2026 · Phase 84.30
 *
 * "+" on the Analytics header that spawns a daily task from an SEO / ranking
 * insight. The new task drops into the daily-task Kanban and reflects on
 * everyone's dashboard once assigned. Includes a "Link to priority" dropdown
 * so the task auto-rolls up under a project priority (Phase 84.30 rule).
 */
import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const ANY = "__any__";
const QUICK_TEMPLATES = [
  "Improve SEO for top-loss page",
  "Get 3 backlinks this week",
  "Optimize meta descriptions for top 5 pages",
  "Boost trending post via ads",
  "Refresh top-clicked but low-conversion page",
];

export default function AnalyticsAddTaskButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [projectId, setProjectId] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [priorityId, setPriorityId] = useState("");
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    Promise.all([
      api.get("/daily-tasks/projects").then((r) => r.data || []).catch(() => []),
      api.get("/users?kind=internal").then((r) => r.data || []).catch(() => []),
      api.get("/priorities/all-open").then((r) => r.data || []).catch(() => []),
    ]).then(([p, m, pr]) => {
      setProjects(p.filter((x) => x.daily_task_sync));
      setMembers(m.filter((u) => (u.status || "active") === "active"));
      setPriorities(pr);
    });
    // Default assignee = current user for fast one-clicks.
    setAssigneeId(user?.id || "");
    setDueDate((d) => d || new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10));  // Phase 101 — due_date is mandatory
  }, [open, user]);

  const submit = useCallback(async () => {
    if (!title.trim()) { toast.error("Task title is required"); return; }
    if (!dueDate) { toast.error("Due date is required"); return; }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        description: description || undefined,
        due_date: dueDate,
        project_id: projectId || undefined,
        assignee_id: assigneeId || undefined,
        priority_id: priorityId || undefined,
      };
      await api.post("/daily-tasks", payload);
      toast.success("Task created · lands in the Kanban");
      setTitle(""); setDescription(""); setDueDate("");
      setProjectId(""); setPriorityId("");
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }, [title, description, dueDate, projectId, assigneeId, priorityId]);

  return (
    <>
      <Button variant="outline" className="rounded-sm bg-[#F54800] text-white border-[#F54800] hover:bg-[#D63E00]" onClick={() => setOpen(true)} data-testid="analytics-add-task">
        <Plus size={13} className="mr-1.5" /> New task
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="analytics-add-task-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-base">Turn an insight into a task</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-1">
            <div>
              <Label className="label-overline">Title</Label>
              <Input className="rounded-sm mt-1" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Improve SEO for /pricing page" data-testid="analytics-task-title" />
              <div className="flex flex-wrap gap-1 mt-1.5">
                {QUICK_TEMPLATES.map((t) => (
                  <button key={t} type="button" onClick={() => setTitle(t)} className="text-[10px] px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600 hover:bg-slate-200" data-testid={`analytics-task-template-${t.slice(0, 10)}`}>+ {t}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="label-overline">Details</Label>
              <Textarea rows={2} className="rounded-sm mt-1 resize-none" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Link to the analytics chart, page URL, etc." data-testid="analytics-task-description" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="label-overline">Due date <span className="text-[#F54800]">*</span></Label>
                <Input type="date" className="rounded-sm mt-1" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="analytics-task-due" />
              </div>
              <div>
                <Label className="label-overline">Assignee</Label>
                <Select value={assigneeId || ANY} onValueChange={(v) => setAssigneeId(v === ANY ? "" : v)}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="analytics-task-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ANY}>— Unassigned —</SelectItem>
                    {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="label-overline">Project</Label>
                <Select value={projectId || ANY} onValueChange={(v) => setProjectId(v === ANY ? "" : v)}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="analytics-task-project"><SelectValue placeholder="No project" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ANY}>— No project —</SelectItem>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-overline">Priority (link)</Label>
                <Select value={priorityId || ANY} onValueChange={(v) => setPriorityId(v === ANY ? "" : v)}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="analytics-task-priority"><SelectValue placeholder="No priority" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ANY}>— No priority —</SelectItem>
                    {priorities.map((p) => <SelectItem key={p.id} value={p.id}>{p.title} · {p.project_name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[9px] text-slate-400 mt-0.5">Priority auto-closes when all linked tasks are done — auto-reopens if a new task is added later.</p>
              </div>
            </div>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={submit} disabled={saving || !title.trim() || !dueDate} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="analytics-task-create">
              {saving && <Loader2 size={12} className="animate-spin mr-1" />}Create task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
